import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useNexaStore } from '../NexaStore';
import { consumePendingAskRequest } from '../utils/askNexaStore';
import { logger } from '@src/utils/misc/logger';
import { NexaAskRequest } from '../types/askNexa';

interface UseAskNexaInitResult {
  initialQuery?: string;
  sessionReady: boolean;
  askRequest?: NexaAskRequest;
}

/**
 * Custom hook to handle "Ask Nexa" initialization from various dashboard contexts
 *
 * Responsibilities:
 * 1. Extract and validate Ask Nexa request from router state
 * 2. Determine when session is ready for query execution
 * 3. Provide extensibility point for type-specific initialization logic
 *
 * @param isFetching - Whether the session is currently being fetched
 * @returns Object containing initialQuery, sessionReady flag, and full askRequest
 */
export const useAskNexaInit = (isFetching: boolean): UseAskNexaInitResult => {
  const { id } = useParams<{ id: string }>();

  // Check if store is initialized by verifying the session ID matches
  const storeSessionId = useNexaStore((state) => state.id);
  const sessionReady = !isFetching && storeSessionId === id;

  // Extract Ask Nexa request from in-memory store (only computed once on mount)
  // Using store instead of router state to avoid auto-trigger on tab duplication
  const askRequest = useMemo(() => {
    if (!id) {
      return undefined;
    }

    const request = consumePendingAskRequest(id);

    if (request) {
      logger.log('[useAskNexaInit] Found Ask Nexa request from store:', request);

      // Validate request structure
      if (!request.diagnostic || !request.diagnostic.type || !request.query) {
        logger.error('[useAskNexaInit] Invalid Ask Nexa request structure:', request);
        return undefined;
      }

      return request;
    }

    return undefined;
  }, []); // Empty deps - only compute once on mount

  // Extract initial query from request
  const initialQuery = useMemo(() => {
    if (!askRequest) {
      return undefined;
    }

    // Type-specific query processing can be added here
    switch (askRequest.diagnostic.type) {
      case 'slice-diagnostics':
        // Could add slice-specific preprocessing here
        logger.log('[useAskNexaInit] Processing slice diagnostics request');
        return askRequest.query;

      case 'error-diagnostics':
        // Could add error-specific preprocessing here
        logger.log('[useAskNexaInit] Processing error diagnostics request');
        return askRequest.query;

      case 'summary-threshold':
        // Could add threshold-specific preprocessing here
        logger.log('[useAskNexaInit] Processing summary threshold request');
        return askRequest.query;

      default:
        // Generic handling for unknown types
        logger.log('[useAskNexaInit] Processing generic request type:', askRequest.diagnostic.type);
        return askRequest.query;
    }
  }, [askRequest]);

  // Future extensibility: Type-specific side effects can be added here
  // Examples:
  // - Pre-loading specific dashboard configurations
  // - Setting up filters or context based on params
  // - Initializing visualizations for certain diagnostic types
  // - Tracking analytics for different diagnostic types
  // - Setting up custom UI states or layouts
  //
  // Example implementation:
  // useEffect(() => {
  //   if (askRequest?.type === 'slice-diagnostics') {
  //     // Pre-configure dashboard for slice analysis
  //     setDashboardConfig(askRequest.params);
  //   }
  // }, [askRequest]);

  return {
    initialQuery,
    sessionReady,
    askRequest, // Return full request for potential future use
  };
};
